import { Fragment, useDeferredValue, useEffect, useRef, type ReactNode } from "react";
import { useLanguageContext } from "state/languageState/languageContext";

/**
 * Remounts the screen subtree when the language changes, so every mounted screen re-runs
 * its `i18n.t` calls. Nothing in the app subscribes to `languageChanged`, so without this
 * a screen keeps the words it resolved when it last rendered.
 *
 * Applies on every platform: no path cascades a language change on web either.
 *
 * Keys on a deferred language so React builds the new tree at transition priority while
 * the old one stays on screen. The loading state is raised by languageContext, which also
 * covers i18next's chunk fetch before this gate has anything to do.
 *
 * Remounting discards screen state: scroll position, expanded rows, un-synced form input.
 */
export function LanguageRemountGate({ children }: { children: ReactNode }) {
  const selectedLanguage = useLanguageContext((s) => s.selectedLanguage);
  const setPendingLanguage = useLanguageContext((s) => s.setPendingLanguage);

  const deferredLanguage = useDeferredValue(selectedLanguage);
  const settled = useRef(true);

  useEffect(() => {
    // Ends the wait when the rendered tree has swapped language. Keyed on the rendered
    // language, not compared against the requested one: i18next resolves what it is given
    // (`pt-BR` may settle as `pt`), so an equality check would never come true.
    if (settled.current) {
      settled.current = false;
      return;
    }
    setPendingLanguage(null);
  }, [deferredLanguage, setPendingLanguage]);

  return <Fragment key={deferredLanguage}>{children}</Fragment>;
}
